import jsPDF from "jspdf";
import autoTable, { type CellHookData } from "jspdf-autotable";
import type { FinancialStatement } from "@shared/schema";
import i18n from "@/i18n";
import { fmtDate } from "@/lib/locale";

// ─── jsPDF type augmentation ──────────────────────────────────────────────────

interface JsPDFWithAutoTable extends jsPDF {
  lastAutoTable: { finalY: number };
}

// ─── Shared Data Types ─────────────────────────────────────────────────────────

export interface CustomLineItem {
  id: string;
  section: string;
  label: string;
  amount: number | "";
}

export interface FloorplanLender {
  id: string;
  lenderName: string;
  balance: number | "";
}

// ─── Numeric / formatting helpers ──────────────────────────────────────────────

export const n = (v: number | string | null | undefined, def = 0): number => {
  if (v == null || v === "") return def;
  const p = parseFloat(String(v));
  return isNaN(p) ? def : p;
};

export const fmt$ = (v: number, d = 0) =>
  `$${v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })}`;

export const fmtPct = (v: number, d = 1) => `${v.toFixed(d)}%`;

// ─── P&L Data Types ───────────────────────────────────────────────────────────

export interface PLData {
  periodStart: string;
  periodEnd: string;
  dealershipName: string;
  address: string;
  businessState: string;
  unitsSold: number | "";
  newVehicleRevenue: number | "";
  usedRetailRevenue: number | "";
  wholesaleRevenue: number | "";
  fiRevenue: number | "";
  docFeesCollected: number | "";
  servicePartsRevenue: number | "";
  bhphPaymentIncome: number | "";
  totalRevenue: number | "";
  newVehicleCogs: number | "";
  usedRetailCogs: number | "";
  wholesaleCogs: number | "";
  packPerUnit: number | "";
  auctionFees: number | "";
  otherCogs: number | "";
  costOfGoodsSold: number | "";
  grossProfit: number | "";
  avgFrontEndGross: number | "";
  avgBackEndGross: number | "";
  payroll: number | "";
  rentMortgage: number | "";
  utilities: number | "";
  advertising: number | "";
  insurance: number | "";
  dmsFees: number | "";
  dealerPrincipalComp: number | "";
  otherOpEx: number | "";
  bhphReposCosts: number | "";
  bhphReserveIncome: number | "";
  floorplanInterest: number | "";
  floorplanFees: number | "";
  reconCosts: number | "";
  transportCosts: number | "";
  customLineItems: CustomLineItem[];
}

export interface PLRatios {
  grossMarginPct: number | null;
  totalExpenseRatio: number | null;
  floorplanAsPctRevenue: number | null;
  floorplanAsPctGross: number | null;
  costToCarryPerUnit: number | null;
  netOperatingIncome: number | null;
  netMarginPct: number | null;
  grossProfitPerUnit: number | null;
  pvr: number | null;
  bhphIncomeAsPctRevenue: number | null;
}

export function computePLRatios(d: PLData): PLRatios {
  // totalRevenue from step 2 does not include BHPH income (entered in step 5);
  // add it here so KPI ratios use the full revenue denominator
  const rev = n(d.totalRevenue) + n(d.bhphPaymentIncome) + n(d.bhphReserveIncome);
  const cogs = n(d.costOfGoodsSold);
  const gp = n(d.grossProfit) || (rev - cogs);
  const units = n(d.unitsSold);
  const floorInterest = n(d.floorplanInterest);
  const floorFees = n(d.floorplanFees);
  const totalFloor = floorInterest + floorFees
    + d.customLineItems.filter(i => i.section === "pl-floorplan").reduce((s, i) => s + n(i.amount), 0);
  const opEx = n(d.payroll) + n(d.rentMortgage) + n(d.utilities) + n(d.advertising) + n(d.insurance) + n(d.dealerPrincipalComp) + n(d.otherOpEx) + n(d.dmsFees)
    + d.customLineItems.filter(i => i.section === "pl-opex").reduce((s, i) => s + n(i.amount), 0);
  const bhphCosts = n(d.bhphReposCosts)
    + d.customLineItems.filter(i => i.section === "pl-bhph").reduce((s, i) => s + n(i.amount), 0);
  const totalExp = opEx + totalFloor + n(d.reconCosts) + n(d.transportCosts) + bhphCosts;
  const bhphIncome = n(d.bhphPaymentIncome) + n(d.bhphReserveIncome);

  const frontGross = n(d.avgFrontEndGross);
  const backGross = n(d.avgBackEndGross);
  const pvr = (frontGross > 0 || backGross > 0) ? frontGross + backGross : null;

  const bhphIncomeAsPctRevenue = rev > 0 && bhphIncome > 0 ? (bhphIncome / rev) * 100 : null;

  return {
    grossMarginPct: rev > 0 ? (gp / rev) * 100 : null,
    totalExpenseRatio: rev > 0 ? (totalExp / rev) * 100 : null,
    floorplanAsPctRevenue: rev > 0 ? (totalFloor / rev) * 100 : null,
    floorplanAsPctGross: gp > 0 ? (totalFloor / gp) * 100 : null,
    costToCarryPerUnit: units > 0 ? totalFloor / units : null,
    netOperatingIncome: gp > 0 || opEx > 0 ? gp - totalExp : null,
    netMarginPct: rev > 0 && gp > 0 ? ((gp - totalExp) / rev) * 100 : null,
    grossProfitPerUnit: units > 0 && gp > 0 ? gp / units : null,
    pvr,
    bhphIncomeAsPctRevenue,
  };
}

// ─── Balance Sheet Data Types ─────────────────────────────────────────────────

export interface BSData {
  periodStart: string;
  periodEnd: string;
  dealershipName: string;
  address: string;
  businessState: string;
  cpaPrepared: boolean;
  cashAndEquivalents: number | "";
  accountsReceivable: number | "";
  notesReceivableShortTerm: number | "";
  citAsset: number | "";
  holdbackReceivable: number | "";
  fiReservesReceivable: number | "";
  retailInventoryValue: number | "";
  wholesaleInventoryValue: number | "";
  prepaidExpenses: number | "";
  otherCurrentAssets: number | "";
  equipmentValue: number | "";
  realPropertyValue: number | "";
  otherLongTermAssets: number | "";
  floorplanPayable: number | "";
  floorplanByLender: FloorplanLender[];
  accountsPayable: number | "";
  accruedExpenses: number | "";
  shortTermNotesPayable: number | "";
  currentPortionLongTermDebt: number | "";
  otherCurrentLiabilities: number | "";
  realEstateLoanPayable: number | "";
  equipmentLoanPayable: number | "";
  longTermDebt: number | "";
  notesPayable: number | "";
  contributedCapital: number | "";
  retainedEarnings: number | "";
  customLineItems: CustomLineItem[];
}

export interface BSTotals {
  currentAssets: number;
  longTermAssets: number;
  totalAssets: number;
  currentLiabilities: number;
  longTermLiabilities: number;
  totalLiabilities: number;
  contributedCapital: number;
  retainedEarnings: number;
  ownersEquity: number;
  isBalanced: boolean;
  inventoryValue: number;
}

export function computeBSTotals(d: BSData): BSTotals {
  const inventoryValue = n(d.retailInventoryValue) + n(d.wholesaleInventoryValue);
  const currentAssets = n(d.cashAndEquivalents) + n(d.accountsReceivable) + n(d.citAsset) + n(d.holdbackReceivable) + n(d.fiReservesReceivable) + inventoryValue + n(d.prepaidExpenses) + n(d.otherCurrentAssets)
    + d.customLineItems.filter(i => i.section === "bs-current-assets").reduce((s, i) => s + n(i.amount), 0);
  const longTermAssets = n(d.equipmentValue) + n(d.realPropertyValue) + n(d.otherLongTermAssets)
    + d.customLineItems.filter(i => i.section === "bs-lt-assets").reduce((s, i) => s + n(i.amount), 0);
  const totalAssets = currentAssets + longTermAssets;

  const floorplanTotal = d.floorplanByLender.length > 0
    ? d.floorplanByLender.reduce((s, l) => s + n(l.balance), 0)
    : n(d.floorplanPayable);

  const currentLiabilities = floorplanTotal + n(d.accountsPayable) + n(d.accruedExpenses) + n(d.shortTermNotesPayable ?? "") + n(d.currentPortionLongTermDebt ?? "") + n(d.otherCurrentLiabilities)
    + d.customLineItems.filter(i => i.section === "bs-current-liab").reduce((s, i) => s + n(i.amount), 0);
  const longTermLiabilities = n(d.realEstateLoanPayable) + n(d.equipmentLoanPayable) + n(d.longTermDebt) + n(d.notesPayable)
    + d.customLineItems.filter(i => i.section === "bs-lt-liab").reduce((s, i) => s + n(i.amount), 0);
  const totalLiabilities = currentLiabilities + longTermLiabilities;
  const contributedCapitalVal = n(d.contributedCapital);
  const retainedEarningsVal = n(d.retainedEarnings);
  const ownersEquity = totalAssets - totalLiabilities - contributedCapitalVal - retainedEarningsVal;
  const isBalanced = totalAssets > 0;
  return { currentAssets, longTermAssets, totalAssets, currentLiabilities, longTermLiabilities, totalLiabilities, contributedCapital: contributedCapitalVal, retainedEarnings: retainedEarningsVal, ownersEquity, isBalanced, inventoryValue };
}

// ─── PDF Helpers ──────────────────────────────────────────────────────────────

function buildPDFHeader(
  doc: JsPDFWithAutoTable,
  title: string,
  dealershipName: string | undefined,
  address: string | undefined,
  businessState: string | undefined,
  periodStart: string,
  periodEnd: string,
) {
  const grey: [number, number, number] = [100, 116, 139];
  const dark: [number, number, number] = [15, 23, 42];
  const blue: [number, number, number] = [37, 99, 235];
  const MARGIN = 14;
  const PAGE_W = 215.9;
  const CONTENT_W = PAGE_W - MARGIN * 2;

  doc.setFillColor(...blue);
  doc.rect(0, 0, PAGE_W, 28, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text("DealFlow", MARGIN, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(title, MARGIN, 19.5);

  if (dealershipName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...dark);
    doc.text(dealershipName, MARGIN, 36);
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...grey);
  const addrParts = [address, businessState].filter(Boolean).join(", ");
  if (addrParts) doc.text(addrParts, MARGIN, 41);
  const periodLabel = periodStart && periodEnd
    ? `${fmtDate(periodStart)} – ${fmtDate(periodEnd)}`
    : periodStart ? fmtDate(periodStart) : periodEnd ? fmtDate(periodEnd) : "—";
  doc.text(i18n.t("financial:pdf.periodLabel", { range: periodLabel }), MARGIN, 46);

  return { grey, dark, blue, MARGIN, CONTENT_W };
}

function addPDFFooters(doc: jsPDF, grey: [number, number, number]) {
  const totalPages = (doc as unknown as { internal: { getNumberOfPages: () => number } }).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...grey);
    doc.text(i18n.t("financial:pdf.footer"), 14, 272);
    doc.text(i18n.t("financial:pdf.pageOf", { current: i, total: totalPages }), 195, 272, { align: "right" });
  }
}

// ─── PL PDF Export ────────────────────────────────────────────────────────────

export function exportPLPDF(data: PLData) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" }) as JsPDFWithAutoTable;
  const { grey, dark, blue, MARGIN, CONTENT_W } = buildPDFHeader(
    doc, i18n.t("financial:pdf.titlePL"),
    data.dealershipName, data.address, data.businessState, data.periodStart, data.periodEnd
  );

  let y = 52;

  const sectionHeader = (title: string) => {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFillColor(...blue);
    doc.rect(MARGIN, y, CONTENT_W, 6, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(title.toUpperCase(), MARGIN + 3, y + 4.2);
    y += 8;
  };

  const dataRows = (rows: [string, string][], boldLast = false) => {
    if (rows.length === 0) return;
    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: CONTENT_W,
      head: [],
      body: rows,
      styles: { fontSize: 8, cellPadding: { top: 2, bottom: 2, left: 3, right: 3 } },
      columnStyles: { 0: { textColor: grey, fontStyle: "normal" }, 1: { halign: "right", fontStyle: "normal", textColor: dark } },
      didParseCell: (hook: CellHookData) => {
        if (boldLast && hook.row.index === rows.length - 1) {
          hook.cell.styles.fontStyle = "bold";
          (hook.cell.styles as unknown as Record<string, unknown>).textColor = dark;
        }
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    y = (doc as JsPDFWithAutoTable).lastAutoTable.finalY + 6;
  };

  // Include BHPH payment income + reserve income in total revenue for PDF consistency
  const rev = n(data.totalRevenue) + n(data.bhphPaymentIncome) + n(data.bhphReserveIncome);
  const cogs = n(data.costOfGoodsSold);
  const gp = n(data.grossProfit) || (rev - cogs);
  const units = n(data.unitsSold);
  const opEx = n(data.payroll) + n(data.rentMortgage) + n(data.utilities) + n(data.advertising) + n(data.insurance) + n(data.dmsFees) + n(data.dealerPrincipalComp) + n(data.otherOpEx)
    + data.customLineItems.filter(i => i.section === "pl-opex").reduce((s, i) => s + n(i.amount), 0);
  const floorTotal = n(data.floorplanInterest) + n(data.floorplanFees)
    + data.customLineItems.filter(i => i.section === "pl-floorplan").reduce((s, i) => s + n(i.amount), 0);
  const otherCosts = n(data.reconCosts) + n(data.transportCosts);
  const bhphCosts = n(data.bhphReposCosts)
    + data.customLineItems.filter(i => i.section === "pl-bhph").reduce((s, i) => s + n(i.amount), 0);
  const totalExp = opEx + floorTotal + otherCosts + bhphCosts;
  const noi = gp - totalExp;
  const ratios = computePLRatios(data);
  const hasBHPH = n(data.bhphPaymentIncome) > 0 || n(data.bhphReposCosts) > 0 || n(data.bhphReserveIncome) > 0;
  const customRevItems = data.customLineItems.filter(i => i.section === "pl-revenue");
  const customCogsItems = data.customLineItems.filter(i => i.section === "pl-cogs");
  const customOpexItems = data.customLineItems.filter(i => i.section === "pl-opex");
  const customFloorItems = data.customLineItems.filter(i => i.section === "pl-floorplan");
  const customBHPHItems = data.customLineItems.filter(i => i.section === "pl-bhph");

  sectionHeader(i18n.t("financial:pdf.sections.revenueBreakdown"));
  dataRows([
    ...(n(data.newVehicleRevenue) > 0 ? [[i18n.t("financial:pdf.rows.newVehicleRevenue"), fmt$(n(data.newVehicleRevenue))] as [string, string]] : []),
    [i18n.t("financial:pdf.rows.usedRetailSalesRevenue"), n(data.usedRetailRevenue) > 0 ? fmt$(n(data.usedRetailRevenue)) : "—"],
    ...(n(data.wholesaleRevenue) > 0 ? [[i18n.t("financial:pdf.rows.wholesaleRevenue"), fmt$(n(data.wholesaleRevenue))] as [string, string]] : []),
    [i18n.t("financial:pdf.rows.fiIncome"), n(data.fiRevenue) > 0 ? fmt$(n(data.fiRevenue)) : "—"],
    ...(n(data.docFeesCollected) > 0 ? [[i18n.t("financial:pdf.rows.docFeesCollected"), fmt$(n(data.docFeesCollected))] as [string, string]] : []),
    ...(n(data.servicePartsRevenue) > 0 ? [[i18n.t("financial:pdf.rows.servicePartsRevenue"), fmt$(n(data.servicePartsRevenue))] as [string, string]] : []),
    ...(n(data.bhphPaymentIncome) > 0 ? [[i18n.t("financial:pdf.rows.bhphPaymentIncome"), fmt$(n(data.bhphPaymentIncome))] as [string, string]] : []),
    ...customRevItems.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    [i18n.t("financial:pdf.rows.totalRevenue"), rev > 0 ? fmt$(rev) : "—"],
    [i18n.t("financial:pdf.rows.unitsSold"), units > 0 ? String(units) : "—"],
    ...(n(data.avgFrontEndGross) > 0 ? [[i18n.t("financial:pdf.rows.avgFrontEndGross"), fmt$(n(data.avgFrontEndGross))] as [string, string]] : []),
    ...(n(data.avgBackEndGross) > 0 ? [[i18n.t("financial:pdf.rows.avgBackEndGross"), fmt$(n(data.avgBackEndGross))] as [string, string]] : []),
  ], true);

  sectionHeader(i18n.t("financial:pdf.sections.cogs"));
  dataRows([
    ...(n(data.newVehicleCogs) > 0 ? [[i18n.t("financial:pdf.rows.newVehicleCosts"), fmt$(n(data.newVehicleCogs))] as [string, string]] : []),
    [i18n.t("financial:pdf.rows.usedRetailVehicleCosts"), n(data.usedRetailCogs) > 0 ? fmt$(n(data.usedRetailCogs)) : "—"],
    ...(n(data.wholesaleCogs) > 0 ? [[i18n.t("financial:pdf.rows.wholesaleVehicleCosts"), fmt$(n(data.wholesaleCogs))] as [string, string]] : []),
    ...(n(data.packPerUnit) > 0 ? [[i18n.t("financial:pdf.rows.packPerUnit", { units }), fmt$(n(data.packPerUnit) * units)] as [string, string]] : []),
    ...(n(data.auctionFees) > 0 ? [[i18n.t("financial:pdf.rows.auctionFees"), fmt$(n(data.auctionFees))] as [string, string]] : []),
    ...(n(data.otherCogs) > 0 ? [[i18n.t("financial:pdf.rows.otherCogs"), fmt$(n(data.otherCogs))] as [string, string]] : []),
    ...customCogsItems.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    [i18n.t("financial:pdf.rows.totalCogs"), cogs > 0 ? fmt$(cogs) : "—"],
    [i18n.t("financial:pdf.rows.grossProfit"), gp > 0 ? fmt$(gp) : "—"],
  ], true);

  sectionHeader(i18n.t("financial:pdf.sections.operatingExpenses"));
  dataRows([
    [i18n.t("financial:pdf.rows.payroll"), n(data.payroll) > 0 ? fmt$(n(data.payroll)) : "—"],
    [i18n.t("financial:pdf.rows.rentMortgage"), n(data.rentMortgage) > 0 ? fmt$(n(data.rentMortgage)) : "—"],
    [i18n.t("financial:pdf.rows.utilities"), n(data.utilities) > 0 ? fmt$(n(data.utilities)) : "—"],
    [i18n.t("financial:pdf.rows.advertising"), n(data.advertising) > 0 ? fmt$(n(data.advertising)) : "—"],
    [i18n.t("financial:pdf.rows.insurance"), n(data.insurance) > 0 ? fmt$(n(data.insurance)) : "—"],
    ...(n(data.dmsFees) > 0 ? [[i18n.t("financial:pdf.rows.dmsFees"), fmt$(n(data.dmsFees))] as [string, string]] : []),
    ...(n(data.dealerPrincipalComp) > 0 ? [[i18n.t("financial:pdf.rows.dealerPrincipalComp"), fmt$(n(data.dealerPrincipalComp))] as [string, string]] : []),
    ...(n(data.otherOpEx) > 0 ? [[i18n.t("financial:pdf.rows.otherOperatingExpenses"), fmt$(n(data.otherOpEx))] as [string, string]] : []),
    ...customOpexItems.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    [i18n.t("financial:pdf.rows.totalOperatingExpenses"), opEx > 0 ? fmt$(opEx) : "—"],
  ], true);

  if (hasBHPH) {
    sectionHeader(i18n.t("financial:pdf.sections.bhph"));
    dataRows([
      ...(n(data.bhphReposCosts) > 0 ? [[i18n.t("financial:pdf.rows.repoCosts"), fmt$(n(data.bhphReposCosts))] as [string, string]] : []),
      ...(n(data.bhphReserveIncome) > 0 ? [[i18n.t("financial:pdf.rows.reserveIncome"), fmt$(n(data.bhphReserveIncome))] as [string, string]] : []),
      ...customBHPHItems.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    ]);
  }

  sectionHeader(i18n.t("financial:pdf.sections.floorplanHolding"));
  dataRows([
    [i18n.t("financial:pdf.rows.floorplanInterest"), n(data.floorplanInterest) > 0 ? fmt$(n(data.floorplanInterest)) : "—"],
    [i18n.t("financial:pdf.rows.floorplanFees"), n(data.floorplanFees) > 0 ? fmt$(n(data.floorplanFees)) : "—"],
    [i18n.t("financial:pdf.rows.reconditioningCosts"), n(data.reconCosts) > 0 ? fmt$(n(data.reconCosts)) : "—"],
    [i18n.t("financial:pdf.rows.transportAuctionFees"), n(data.transportCosts) > 0 ? fmt$(n(data.transportCosts)) : "—"],
    ...customFloorItems.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    [i18n.t("financial:pdf.rows.totalFloorplanHolding"), floorTotal + otherCosts > 0 ? fmt$(floorTotal + otherCosts) : "—"],
  ], true);

  sectionHeader(i18n.t("financial:pdf.sections.netIncome"));
  dataRows([
    [i18n.t("financial:pdf.rows.totalRevenue"), rev > 0 ? fmt$(rev) : "—"],
    [i18n.t("financial:pdf.rows.totalExpenses"), totalExp > 0 ? fmt$(totalExp) : "—"],
    [i18n.t("financial:pdf.rows.noi"), gp > 0 ? fmt$(noi) : "—"],
    ...(ratios.netMarginPct !== null ? [[i18n.t("financial:pdf.rows.netMargin"), fmtPct(ratios.netMarginPct)] as [string, string]] : []),
  ], true);

  if (y > 200) { doc.addPage(); y = 20; }

  sectionHeader(i18n.t("financial:pdf.sections.kpiSummary"));
  dataRows([
    [i18n.t("financial:pdf.rows.grossMargin"), ratios.grossMarginPct !== null ? fmtPct(ratios.grossMarginPct) : "—"],
    [i18n.t("financial:pdf.rows.totalExpenseRatio"), ratios.totalExpenseRatio !== null ? fmtPct(ratios.totalExpenseRatio) : "—"],
    [i18n.t("financial:pdf.rows.floorplanPctGross"), ratios.floorplanAsPctGross !== null ? fmtPct(ratios.floorplanAsPctGross) : "—"],
    [i18n.t("financial:pdf.rows.floorplanPctRevenue"), ratios.floorplanAsPctRevenue !== null ? fmtPct(ratios.floorplanAsPctRevenue) : "—"],
    [i18n.t("financial:pdf.rows.pvr"), ratios.pvr !== null ? fmt$(ratios.pvr, 2) : "—"],
    [i18n.t("financial:pdf.rows.grossProfitPerUnit"), ratios.grossProfitPerUnit !== null ? fmt$(ratios.grossProfitPerUnit, 2) : "—"],
    ...(hasBHPH && ratios.bhphIncomeAsPctRevenue !== null ? [[i18n.t("financial:pdf.rows.bhphIncomePct"), fmtPct(ratios.bhphIncomeAsPctRevenue)] as [string, string]] : []),
  ]);

  addPDFFooters(doc, grey);

  const safeName = (data.dealershipName || "pl-statement").replace(/[^a-z0-9]/gi, "-").toLowerCase();
  doc.save(`${safeName}-pl-${data.periodStart}-${data.periodEnd}.pdf`);
}

// ─── Balance Sheet PDF Export ──────────────────────────────────────────────────

export function exportBSPDF(data: BSData) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" }) as JsPDFWithAutoTable;
  const { grey, dark, blue, MARGIN, CONTENT_W } = buildPDFHeader(
    doc, i18n.t("financial:pdf.titleBS"),
    data.dealershipName, data.address, data.businessState, data.periodStart, data.periodEnd
  );
  const totals = computeBSTotals(data);

  let y = 52;

  const sectionHeader = (title: string) => {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFillColor(...blue);
    doc.rect(MARGIN, y, CONTENT_W, 6, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(title.toUpperCase(), MARGIN + 3, y + 4.2);
    y += 8;
  };

  const dataRows = (rows: [string, string][], boldLast = false) => {
    if (rows.length === 0) return;
    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: CONTENT_W,
      head: [],
      body: rows,
      styles: { fontSize: 8, cellPadding: { top: 2, bottom: 2, left: 3, right: 3 } },
      columnStyles: { 0: { textColor: grey, fontStyle: "normal" }, 1: { halign: "right", fontStyle: "normal", textColor: dark } },
      didParseCell: (hook: CellHookData) => {
        if (boldLast && hook.row.index === rows.length - 1) {
          hook.cell.styles.fontStyle = "bold";
          (hook.cell.styles as unknown as Record<string, unknown>).textColor = dark;
        }
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    y = (doc as JsPDFWithAutoTable).lastAutoTable.finalY + 6;
  };

  const floorplanByLenderTotal = data.floorplanByLender.length > 0
    ? data.floorplanByLender.reduce((s, l) => s + n(l.balance), 0)
    : n(data.floorplanPayable);

  const customCurrentAssets = data.customLineItems.filter(i => i.section === "bs-current-assets");
  const customLTAssets = data.customLineItems.filter(i => i.section === "bs-lt-assets");
  const customCurrentLiab = data.customLineItems.filter(i => i.section === "bs-current-liab");
  const customLTLiab = data.customLineItems.filter(i => i.section === "bs-lt-liab");

  sectionHeader(i18n.t("financial:pdf.sections.currentAssets"));
  dataRows([
    [i18n.t("financial:pdf.rows.cashAndEquivalents"), n(data.cashAndEquivalents) > 0 ? fmt$(n(data.cashAndEquivalents)) : "—"],
    [i18n.t("financial:pdf.rows.accountsReceivable"), n(data.accountsReceivable) > 0 ? fmt$(n(data.accountsReceivable)) : "—"],
    ...(n(data.citAsset) > 0 ? [[i18n.t("financial:pdf.rows.citAsset"), fmt$(n(data.citAsset))] as [string, string]] : []),
    ...(n(data.holdbackReceivable) > 0 ? [[i18n.t("financial:pdf.rows.holdbackReceivable"), fmt$(n(data.holdbackReceivable))] as [string, string]] : []),
    ...(n(data.fiReservesReceivable) > 0 ? [[i18n.t("financial:pdf.rows.fiReservesReceivable"), fmt$(n(data.fiReservesReceivable))] as [string, string]] : []),
    ...(n(data.retailInventoryValue) > 0 ? [[i18n.t("financial:pdf.rows.retailInventory"), fmt$(n(data.retailInventoryValue))] as [string, string]] : []),
    ...(n(data.wholesaleInventoryValue) > 0 ? [[i18n.t("financial:pdf.rows.wholesaleInventory"), fmt$(n(data.wholesaleInventoryValue))] as [string, string]] : []),
    ...(totals.inventoryValue > 0 ? [[i18n.t("financial:pdf.rows.totalInventoryValue"), fmt$(totals.inventoryValue)] as [string, string]] : []),
    ...(n(data.prepaidExpenses) > 0 ? [[i18n.t("financial:pdf.rows.prepaidExpenses"), fmt$(n(data.prepaidExpenses))] as [string, string]] : []),
    ...(n(data.otherCurrentAssets) > 0 ? [[i18n.t("financial:pdf.rows.otherCurrentAssets"), fmt$(n(data.otherCurrentAssets))] as [string, string]] : []),
    ...customCurrentAssets.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    [i18n.t("financial:pdf.rows.totalCurrentAssets"), fmt$(totals.currentAssets)],
  ], true);

  sectionHeader(i18n.t("financial:pdf.sections.longTermAssets"));
  dataRows([
    ...(n(data.equipmentValue) > 0 ? [[i18n.t("financial:pdf.rows.equipmentVehicles"), fmt$(n(data.equipmentValue))] as [string, string]] : []),
    ...(n(data.realPropertyValue) > 0 ? [[i18n.t("financial:pdf.rows.realProperty"), fmt$(n(data.realPropertyValue))] as [string, string]] : []),
    ...(n(data.otherLongTermAssets) > 0 ? [[i18n.t("financial:pdf.rows.otherLongTermAssets"), fmt$(n(data.otherLongTermAssets))] as [string, string]] : []),
    ...customLTAssets.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    [i18n.t("financial:pdf.rows.totalLongTermAssets"), fmt$(totals.longTermAssets)],
  ], true);

  dataRows([[i18n.t("financial:pdf.rows.totalAssetsCaps"), fmt$(totals.totalAssets)]], false);

  sectionHeader(i18n.t("financial:pdf.sections.currentLiabilities"));
  dataRows([
    ...(data.floorplanByLender.length > 0
      ? data.floorplanByLender.map(l => [l.lenderName || i18n.t("financial:pdf.rows.floorplanLender"), n(l.balance) > 0 ? fmt$(n(l.balance)) : "—"] as [string, string])
      : [[i18n.t("financial:pdf.rows.floorplanPayable"), floorplanByLenderTotal > 0 ? fmt$(floorplanByLenderTotal) : "—"] as [string, string]]),
    ...(data.floorplanByLender.length > 0 ? [[i18n.t("financial:pdf.rows.floorplanPayableTotal"), fmt$(floorplanByLenderTotal)] as [string, string]] : []),
    ...(n(data.accountsPayable) > 0 ? [[i18n.t("financial:pdf.rows.accountsPayable"), fmt$(n(data.accountsPayable))] as [string, string]] : []),
    ...(n(data.accruedExpenses) > 0 ? [[i18n.t("financial:pdf.rows.accruedExpenses"), fmt$(n(data.accruedExpenses))] as [string, string]] : []),
    ...(n(data.otherCurrentLiabilities) > 0 ? [[i18n.t("financial:pdf.rows.otherCurrentLiabilities"), fmt$(n(data.otherCurrentLiabilities))] as [string, string]] : []),
    ...customCurrentLiab.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    [i18n.t("financial:pdf.rows.totalCurrentLiabilities"), fmt$(totals.currentLiabilities)],
  ], true);

  sectionHeader(i18n.t("financial:pdf.sections.longTermLiabilities"));
  dataRows([
    ...(n(data.realEstateLoanPayable) > 0 ? [[i18n.t("financial:pdf.rows.realEstateLoan"), fmt$(n(data.realEstateLoanPayable))] as [string, string]] : []),
    ...(n(data.equipmentLoanPayable) > 0 ? [[i18n.t("financial:pdf.rows.equipmentLoan"), fmt$(n(data.equipmentLoanPayable))] as [string, string]] : []),
    ...(n(data.longTermDebt) > 0 ? [[i18n.t("financial:pdf.rows.longTermDebt"), fmt$(n(data.longTermDebt))] as [string, string]] : []),
    ...(n(data.notesPayable) > 0 ? [[i18n.t("financial:pdf.rows.notesPayable"), fmt$(n(data.notesPayable))] as [string, string]] : []),
    ...customLTLiab.filter(i => i.label).map(i => [i.label, n(i.amount) > 0 ? fmt$(n(i.amount)) : "—"] as [string, string]),
    [i18n.t("financial:pdf.rows.totalLongTermLiabilities"), fmt$(totals.longTermLiabilities)],
  ], true);

  dataRows([[i18n.t("financial:pdf.rows.totalLiabilitiesCaps"), fmt$(totals.totalLiabilities)]], false);

  sectionHeader(i18n.t("financial:pdf.sections.ownersEquity"));
  dataRows([
    [i18n.t("financial:pdf.rows.contributedCapital"), n(data.contributedCapital) > 0 ? fmt$(n(data.contributedCapital)) : "—"],
    [i18n.t("financial:pdf.rows.retainedEarnings"), fmt$(n(data.retainedEarnings))],
    [i18n.t("financial:pdf.rows.totalOwnersEquity"), fmt$(totals.ownersEquity)],
  ], true);

  dataRows([[i18n.t("financial:pdf.rows.totalLiabEquityCaps"), fmt$(totals.totalLiabilities + totals.contributedCapital + totals.retainedEarnings + totals.ownersEquity)]], false);

  addPDFFooters(doc, grey);

  const safeName = (data.dealershipName || "balance-sheet").replace(/[^a-z0-9]/gi, "-").toLowerCase();
  doc.save(`${safeName}-balance-sheet-${data.periodStart}-${data.periodEnd}.pdf`);
}

// ─── Statement → PDF Data Builders ─────────────────────────────────────────────

export function buildPLDataFromStatement(s: FinancialStatement): PLData {
  const p = (v: string | number | null | undefined): number | "" =>
    v != null && v !== "" ? parseFloat(String(v)) : "";

  let customLineItems: CustomLineItem[] = [];
  try {
    if (s.customFieldsJson) {
      const parsed = JSON.parse(s.customFieldsJson);
      if (Array.isArray(parsed)) customLineItems = parsed.filter((i: unknown) => {
        const item = i as Record<string, unknown>;
        return item && typeof item === "object" && typeof item.section === "string" && item.section.startsWith("pl-");
      });
    }
  } catch { /* ignore */ }

  return {
    periodStart: s.periodStart,
    periodEnd: s.periodEnd,
    dealershipName: s.dealershipName ?? "",
    address: s.address ?? "",
    businessState: s.businessState ?? "",
    unitsSold: s.unitsSold ?? "",
    newVehicleRevenue: p(s.newVehicleRevenue),
    usedRetailRevenue: p(s.usedRetailRevenue),
    wholesaleRevenue: p(s.wholesaleRevenue),
    fiRevenue: p(s.fiRevenue),
    docFeesCollected: p(s.docFeesCollected),
    servicePartsRevenue: p(s.servicePartsRevenue),
    bhphPaymentIncome: p(s.bhphPaymentIncome),
    totalRevenue: p(s.totalRevenue),
    newVehicleCogs: p(s.newVehicleCogs),
    usedRetailCogs: "",
    wholesaleCogs: "",
    packPerUnit: p(s.packPerUnit),
    auctionFees: p(s.auctionFees),
    otherCogs: "",
    costOfGoodsSold: p(s.costOfGoodsSold),
    grossProfit: p(s.grossProfit),
    avgFrontEndGross: p(s.avgFrontEndGross),
    avgBackEndGross: p(s.avgBackEndGross),
    payroll: p(s.payroll),
    rentMortgage: p(s.rentMortgage),
    utilities: p(s.utilities),
    advertising: p(s.advertising),
    insurance: p(s.insurance),
    dmsFees: p(s.dmsFees),
    dealerPrincipalComp: p(s.dealerPrincipalComp),
    otherOpEx: p(s.otherOpEx),
    bhphReposCosts: p(s.bhphReposCosts),
    bhphReserveIncome: p(s.bhphReserveIncome),
    floorplanInterest: p(s.floorplanInterest),
    floorplanFees: p(s.floorplanFees),
    reconCosts: p(s.reconCosts),
    transportCosts: p(s.transportCosts),
    customLineItems,
  };
}

export function buildBSDataFromStatement(s: FinancialStatement): BSData {
  const p = (v: string | number | null | undefined): number | "" =>
    v != null && v !== "" ? parseFloat(String(v)) : "";

  let customLineItems: CustomLineItem[] = [];
  let floorplanByLender: FloorplanLender[] = [];

  try {
    if (s.customFieldsJson) {
      const parsed = JSON.parse(s.customFieldsJson);
      if (Array.isArray(parsed)) customLineItems = parsed.filter((i: unknown) => {
        const item = i as Record<string, unknown>;
        return item && typeof item === "object" && typeof item.section === "string" && item.section.startsWith("bs-");
      });
    }
  } catch { /* ignore */ }

  try {
    if (s.floorplanByLenderJson) {
      const parsed = JSON.parse(s.floorplanByLenderJson);
      if (Array.isArray(parsed)) floorplanByLender = parsed;
    }
  } catch { /* ignore */ }

  return {
    periodStart: s.periodStart,
    periodEnd: s.periodEnd,
    dealershipName: s.dealershipName ?? "",
    address: s.address ?? "",
    businessState: s.businessState ?? "",
    cpaPrepared: s.cpaPrepared ?? false,
    cashAndEquivalents: p(s.cashAndEquivalents),
    accountsReceivable: p(s.accountsReceivable),
    notesReceivableShortTerm: p(s.notesReceivableShortTerm),
    citAsset: p(s.citAsset),
    holdbackReceivable: p(s.holdbackReceivable),
    fiReservesReceivable: p(s.fiReservesReceivable),
    retailInventoryValue: s.retailInventoryValue != null ? p(s.retailInventoryValue) : (s.inventoryValue != null ? p(s.inventoryValue) : ""),
    wholesaleInventoryValue: p(s.wholesaleInventoryValue),
    prepaidExpenses: p(s.prepaidExpenses),
    otherCurrentAssets: p(s.otherCurrentAssets),
    equipmentValue: p(s.equipmentValue),
    realPropertyValue: p(s.realPropertyValue),
    otherLongTermAssets: p(s.otherLongTermAssets),
    floorplanPayable: p(s.floorplanPayable),
    floorplanByLender,
    accountsPayable: p(s.accountsPayable),
    accruedExpenses: p(s.accruedExpenses),
    shortTermNotesPayable: p(s.shortTermNotesPayable),
    currentPortionLongTermDebt: p(s.currentPortionLongTermDebt),
    otherCurrentLiabilities: p(s.otherCurrentLiabilities),
    realEstateLoanPayable: p(s.realEstateLoanPayable),
    equipmentLoanPayable: p(s.equipmentLoanPayable),
    longTermDebt: p(s.longTermDebt),
    notesPayable: p(s.notesPayable),
    contributedCapital: p(s.contributedCapital),
    retainedEarnings: p(s.retainedEarnings),
    customLineItems,
  };
}
